import { Autocomplete, TextField } from "@mui/material";
import axios, { CancelTokenSource } from "axios";
import { isAfter } from "date-fns";
import { useCallback, useRef, useState } from "react";
import { FiX } from "react-icons/fi";
import Modal from "react-responsive-modal";
import { useHistory } from "react-router";
import * as Yup from "yup";
import { useAuth } from "../../hooks/auth";
import { useToast } from "../../hooks/toast";
import api from "../../services/api";
import {
  buildVisitsReportDatesState,
  buildVisitsReportPayload,
} from "../../utils/buildVisitsReportPayload";
import getValidationErrors from "../../utils/getValidationError";
import refreshToken from "../../utils/refreshToken";
import {
  ButtonsContainer,
  Content,
  DatesContainer,
  FieldContainer,
  FieldsAndDatesContainer,
  TitleContainer,
} from "./styles";

interface IModalVisitsPerOwner {
  open: boolean;
  functionToCloseModal(): void;
}

interface IOwner {
  id: number;
  first_name: string;
  email: string;
}

const DATE_REGEX = /^\d{4}-(((0)[0-9])|((1)[0-2]))-([0-2][0-9]|(3)[0-1])$/;

export default function ModalVisitsPerOwner({
  open,
  functionToCloseModal,
}: IModalVisitsPerOwner) {
  const history = useHistory();
  const { user, dateToExpires, access_token } = useAuth();
  const { addToast } = useToast();

  const [owners, setOwners] = useState<IOwner[]>([]);
  const [selectedOwner, setSelectedOwner] = useState<IOwner | null>(null);
  const [searchInput, setSearchInput] = useState("");
  const [initialDate, setInitialDate] = useState("");
  const [finalDate, setFinalDate] = useState("");
  const [loadingShowScreenRequest, setLoadingShowScreenRequest] =
    useState(false);
  const [loadingExcelRequest, setLoadingExcelRequest] = useState(false);

  const cancelTokenRef = useRef<CancelTokenSource | undefined>(undefined);

  const handleSearchOwners = useCallback(
    async (searchTerm: string) => {
      if (!searchTerm.trim()) {
        setOwners([]);
        return;
      }

      if (cancelTokenRef.current !== undefined) {
        cancelTokenRef.current.cancel("Operation canceled due to new request.");
      }

      cancelTokenRef.current = axios.CancelToken.source();

      try {
        const token = await refreshToken(dateToExpires, access_token);
        api.defaults.headers.authorization = `Bearer ${
          token ?? access_token
        }`;

        const response = await api.get("/api/adm/users", {
          params: {
            role: "owner",
            email: searchTerm,
          },
          cancelToken: cancelTokenRef.current?.token,
        });

        const ownersFromApi =
          response.data?.data?.owners ??
          response.data?.data?.users ??
          response.data?.data?.consultants ??
          [];

        setOwners(Array.isArray(ownersFromApi) ? ownersFromApi : []);
      } catch (err: any) {
        if (axios.isCancel(err)) {
          return;
        }

        if (err?.response?.data?.message) {
          addToast({
            title: err.response.data.message,
            type: "error",
          });
        }
      }
    },
    [access_token, addToast, dateToExpires]
  );

  const validateAndBuildPayload = async (type: "screen" | "excel") => {
    if (!user) {
      history.push("/");
      return null;
    }

    if (initialDate && finalDate && isAfter(new Date(initialDate), new Date(finalDate))) {
      addToast({
        title: "Coloque datas válidas!",
        type: "error",
      });
      return null;
    }

    const data = {
      owner_id: selectedOwner?.id,
      initial_date: initialDate || undefined,
      final_date: finalDate || undefined,
    };

    const schema = Yup.object().shape({
      owner_id: Yup.number().required("Selecione um proprietário!"),
      initial_date: Yup.string()
        .notRequired()
        .test("valid-initial-date", "Coloque uma data válida!", (value) => {
          if (!value) return true;
          return DATE_REGEX.test(value);
        }),
      final_date: Yup.string()
        .notRequired()
        .test("valid-final-date", "Coloque uma data válida!", (value) => {
          if (!value) return true;
          return DATE_REGEX.test(value);
        }),
    });

    await schema.validate(data, {
      abortEarly: false,
    });

    return buildVisitsReportPayload({
      filterKey: "owner_id",
      filterId: selectedOwner!.id,
      type,
      initialDate: initialDate || undefined,
      finalDate: finalDate || undefined,
    });
  };

  const handleShowReport = async () => {
    setLoadingShowScreenRequest(true);

    try {
      const payload = await validateAndBuildPayload("screen");
      if (!payload) {
        setLoadingShowScreenRequest(false);
        return;
      }

      const token = await refreshToken(dateToExpires, access_token);
      api.defaults.headers.authorization = `Bearer ${token ?? access_token}`;

      const response = await api.post(
        "/api/adm/reports/schedulesByOwner",
        payload
      );

      if (response.data.success) {
        history.push({
          pathname: "/show-visits-per-consultant-report",
          state: {
            data: response.data.data,
            dates: buildVisitsReportDatesState(
              initialDate || undefined,
              finalDate || undefined
            ),
            title: "Visitas por Proprietário",
            filterLabel: selectedOwner
              ? `${selectedOwner.first_name} (${selectedOwner.email})`
              : undefined,
          },
        });

        functionToCloseModal();
      }
    } catch (err: any) {
      if (err instanceof Yup.ValidationError) {
        const error = getValidationErrors(err);
        addToast({
          title: error[Object.keys(error)[0]],
          type: "info",
          description: "",
        });
      } else if (err?.response?.data?.message) {
        addToast({
          title: err.response.data.message,
          type: "error",
        });
      }

      console.log(err);
    }

    setLoadingShowScreenRequest(false);
  };

  const handleExcelDownloadReport = async () => {
    setLoadingExcelRequest(true);

    try {
      const payload = await validateAndBuildPayload("excel");
      if (!payload) {
        setLoadingExcelRequest(false);
        return;
      }

      const token = await refreshToken(dateToExpires, access_token);
      api.defaults.headers.authorization = `Bearer ${token ?? access_token}`;

      const response = await api.post(
        "/api/adm/reports/schedulesByOwner",
        payload,
        {
          responseType: "blob",
        }
      );

      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", "Visitas por proprietario.xlsx");
      link.click();
    } catch (err: any) {
      if (err instanceof Yup.ValidationError) {
        const error = getValidationErrors(err);
        addToast({
          title: error[Object.keys(error)[0]],
          type: "info",
          description: "",
        });
      } else if (err?.response?.data?.message) {
        addToast({
          title: err.response.data.message,
          type: "error",
        });
      }

      console.log(err);
    }

    setLoadingExcelRequest(false);
  };

  return (
    <Modal
      open={open}
      onClose={functionToCloseModal}
      classNames={{
        modal: "modalVisitsPerConsultant",
      }}
      closeIcon={<FiX size={18} color="#000" />}
    >
      <Content>
        <TitleContainer>
          <h3>VISITAS POR PROPRIETÁRIO</h3>
        </TitleContainer>

        <FieldsAndDatesContainer>
          <FieldContainer>
            <p>Proprietário:</p>
            <Autocomplete
              options={owners}
              value={selectedOwner}
              inputValue={searchInput}
              getOptionLabel={(option) =>
                `${option.first_name} - ${option.email}`
              }
              isOptionEqualToValue={(option, value) => option.id === value.id}
              noOptionsText="Sem opções"
              style={{ width: "100%" }}
              onChange={(_event, value) => {
                setSelectedOwner(value);
                if (value) {
                  setSearchInput(`${value.first_name} - ${value.email}`);
                }
              }}
              onInputChange={(_event, value, reason) => {
                if (reason === "input") {
                  setSearchInput(value);
                  handleSearchOwners(value);
                }
                if (reason === "clear") {
                  setSearchInput("");
                  setSelectedOwner(null);
                  setOwners([]);
                }
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  margin="normal"
                  placeholder="Buscar por nome ou e-mail"
                />
              )}
            />
          </FieldContainer>

          <DatesContainer>
            <p>Data inicial:</p>
            <TextField
              id="initial-date"
              type="date"
              InputLabelProps={{
                shrink: true,
              }}
              value={initialDate}
              onChange={(event) => setInitialDate(event.target.value)}
            />

            <p>Data final:</p>
            <TextField
              id="final-date"
              type="date"
              InputLabelProps={{
                shrink: true,
              }}
              value={finalDate}
              onChange={(event) => setFinalDate(event.target.value)}
            />
          </DatesContainer>
        </FieldsAndDatesContainer>

        <ButtonsContainer>
          <button
            onClick={
              loadingExcelRequest ? () => {} : handleExcelDownloadReport
            }
          >
            {loadingExcelRequest ? "Carregando..." : "Excel"}
          </button>
          <button
            onClick={loadingShowScreenRequest ? () => {} : handleShowReport}
          >
            {loadingShowScreenRequest ? "Carregando..." : "Visualizar"}
          </button>
        </ButtonsContainer>
      </Content>
    </Modal>
  );
}
